import { axios } from '/plugins/axios.client';
import { errorFromResponse } from '/src/common/general-utils';
import { i18n } from '/plugins/i18n.client';

const errorHandler = errorFromResponse(i18n.global.t.bind(i18n));
import { toast } from '/plugins/toaster.client.js';

import { getObjectRecord } from '/src/features/object-records/api/object-record-service';

/**
 * Fetch details(phases and steps) of Wizard by UUID
 * @param {String} wizardUuid UUID of the wizard whose details are to be fetched
 */
export const getWizardOverview = async (wizardUuid) => {
  try {
    return await axios.$get(`/api/wizard/overview/${wizardUuid}`);
  } catch (error) {
    toast.error(errorHandler(i18n.global.t('common.form_wizard.fetch_form_wizard'), error));
    return null;
  }
};

/**
 * Fetch form wizard details by UUID
 * @param {String} wizardUuid UUID of the wizard whose details are to be fetched
 */
export const getWizardDetails = async (wizardUuid) => {
  try {
    return await axios.$get(`/api/wizard/${wizardUuid}`);
  } catch (error) {
    toast.error(errorHandler(i18n.global.t('common.form_wizard.fetch_form_wizard'), error));
    return null;
  }
};

/** Fetch the record for form wizard based on WizardType
 * @param {Object} wizardDetails Details of the wizard
 * @param {String} objectUUID UUID of the object
 * @param {String} recordUUID UUID provided in the URL
 * @param {Object} currentPhase Details of current phase
 */
export const getPhaseRecord = async (
  { UUID, WizardType, ObjectUUID: baseObjectUUID },
  objectUUID,
  recordUUID,
  { CurrentObjField, PhaseObjectUUID, UUID: currentPhaseUUID, taskUUID }
) => {
  try {
    // if WizardType is single | baseObject === phaseObject, return the record of the object
    if (WizardType === 'single' || baseObjectUUID === PhaseObjectUUID) {
      return await getObjectRecord(objectUUID, recordUUID, {
        params: { displayFileSize: true, taskUUID }
      });
    }
    // if WizardType is multiple return the record of phase object
    let phaseResponse = await axios.$get(
      `/api/wizard/phase/record/${UUID}/${recordUUID}?currentPhaseUUID=${currentPhaseUUID}`
    );
    // if phase object has already a record mapped with base object, get the record based on that UUID
    if (phaseResponse.UUID) {
      return await getObjectRecord(objectUUID, phaseResponse.UUID, {
        params: { displayFileSize: true, taskUUID }
      });
    } else if (phaseResponse.baseObjFieldValue) {
      // if phase has no record mapped, assign the base mapped field value in the new phase record
      return {
        [CurrentObjField]: Object.values(phaseResponse.baseObjFieldValue)[0]
      };
    }
    return {};
  } catch (error) {
    toast.error(errorHandler('common.object_record.fetch_object_record', error));
    return null;
  }
};

/**
 * Saves the record in base object
 * @param {String} objectUUID UUID of the base object
 */
export const saveBaseObjectRecord = async (objectUUID) => {
  try {
    return (await axios.$post(`/api/object/${objectUUID}/records`)).UUID;
  } catch (error) {
    toast.error(errorHandler('common.form_wizard.save_base_object', error));
    return null;
  }
};

/** Fetch last saved step
 * @param {String} recordUUID UUID provided in the URL
 * @param {String} wizardUUID UUID of wizard
 * @param {Number} userID ID of a user
 * @param {String} [taskUUID] UUID of task
 */
export const getLastSavedStep = async (recordUUID, wizardUUID, userID, taskUUID) => {
  try {
    return await axios.$post(`/api/wizard/getLastSavedStep/`, { recordUUID, wizardUUID, userID, taskUUID });
  } catch (error) {
    toast.error(errorHandler('common.form_wizard.fetch_last_saved_step', error));
    return null;
  }
};

/**
 * update last saved step
 * @param {Object} context
 * @param {String} context.recordUUID
 * @param {String} context.wizardUUID
 * @param {String} [context.taskUUID]
 * @param {Number} userId
 * @param {Object} stepData
 */
export const updateLastSavedStep = async ({ recordUUID, wizardUUID, taskUUID }, userId, stepData) => {
  try {
    if (recordUUID !== 'add') {
      return await axios.$post(`/api/wizard/lastSavedStep`, {
        recordUUID,
        wizardUUID,
        userId,
        stepUUID: stepData.stepUUID,
        stepVisitation: stepData.StepVisitation,
        taskUUID
      });
    }
    return null;
  } catch (error) {
    toast.error(errorHandler('common.form_wizard.update_last_saved_step', error));
    return null;
  }
};

// Check if record is exists or not in target object for multiple object form wizard.
export const checkPhaseUUIDExists = async (UUID, recordUUID, currentPhaseUUID) => {
  try {
    return await axios.$get(`/api/wizard/phase/record/${UUID}/${recordUUID}?currentPhaseUUID=${currentPhaseUUID}`);
  } catch (err) {
    toast.error(errorHandler('common.object_record.fetch_object_record', err));
    return { error: true };
  }
};

export const getDetailsForPhases = async (wizardUUID, recordUUID, taskUUID) => {
  try {
    return await axios.$get(`/api/wizard/getDetailsForPhases/${wizardUUID}/${recordUUID}`, {
      params: { displayFileSize: true, taskUUID }
    });
  } catch (err) {
    return JSON.stringify({ error: true });
  }
};
